// Inspired by https://www.robinwieruch.de/react-hooks-fetch-data/
import { createRef, useCallback, useEffect, useReducer, useState } from "react";
import { batch, useDispatch } from "react-redux";
import { createActions } from "redux-actions";

import rollbar from "lib/rollbar";

import { del, get, patch, post } from "./fetch";

const empty = () => {};

const aborting = createRef(false);
window.addEventListener("beforeunload", () => {
    aborting.current = true;
});

const { asyncComplete, asyncError, asyncStart, asyncSuccess } = createActions(
    "ASYNC_COMPLETE",
    "ASYNC_ERROR",
    "ASYNC_START",
    "ASYNC_SUCCESS",
);

/**
 * Reducer maintains the local state of the current fetch call.
 */
function reducer(state, action) {
    switch (action.type) {
        case asyncStart.toString():
            return {
                data: null,
                error: null,
                isComplete: false,
                isError: false,
                isLoading: true,
                isStarted: true,
                isSuccess: false,
            };

        case asyncSuccess.toString():
            return {
                ...state,
                data: action.payload,
                isSuccess: true,
            };

        case asyncError.toString():
            return {
                ...state,
                error: action.payload,
                isError: true,
            };

        case asyncComplete.toString():
            return {
                ...state,
                isComplete: true,
                isLoading: false,
                init: false,
            };

        default:
            return state;
    }
}

const CALLBACKS = [
    "onStart",
    "onComplete",
    "onError",
    "onSuccess",
    "reduxStart",
    "reduxComplete",
    "reduxError",
    "reduxSuccess",
];

/* eslint-disable no-param-reassign */
/**
 * Ensures that all callbacks are non-null and arrays.
 */
function normalizeCallbacks(options) {
    CALLBACKS.forEach((func) => {
        const orig = options[func];
        if (!Array.isArray(orig)) {
            options[func] = [];
            if (orig) {
                options[func].push(orig);
            }
        }
    });

    return options;
}
/* eslint-enable no-param-reassign */

function mergeOptions(prev, next) {
    const newOptions = normalizeCallbacks({ ...prev });

    CALLBACKS.forEach((func) => {
        if (next[func]) {
            if (next[func] === null) {
                newOptions[func] = [];
            } else {
                newOptions[func] = newOptions[func].concat(next[func]);
            }

            /* eslint-disable no-param-reassign */
            delete next[func];
            /* eslint-enable */
        }
    });

    const data = {
        ...newOptions.data,
        ...(next && next.data),
    };

    return { ...newOptions, ...next, data };
}

/**
 * Probably shouldn't use this directly. All the magic happens here.
 *
 * Calling this will return two params: [runHook, state]
 *
 * You call `runHook(opts ={})` when you want to run the fetch request. You can
 * pass it any additional options that need to be passed through to the call and
 * it will be merged with the initial options that are passed.
 *
 * `state` will contain the current state of the fetch request (see the reducer).
 *
 * An example:
 *
 * const [fetchData, { isLoading }] = useGet('/api/hire/v1/cool/', {
 *     reduxSuccess: hideModal,
 * });
 * fetchData({
 *     onSuccess: () => console.log('done!')
 * });
 *
 * This will run the fetch, then dispatch hideModal() to redux, and log 'done!' in the console
 *
 *
 * Options you can use include:
 *  - data: Data to pass along in the body of the request (or as query params if it's a GET)
 *  - metadata: Additional data passed through to callbacks
 *  - formatResult: Format result before it hits any callbacks
 *  - url: Change the URL for the call
 *  - onComplete: callback to run regardless of success
 *  - onError: callback to run when an error happens
 *  - onSuccess: callback to run when successful
 *  - reduxComplete: a redux action that will be dispatched regardless of success
 *  - reduxError: a redux action that will be dispatched when an error happens
 *  - reduxSuccess: a redux action that will be dispatched when successful
 *
 * Any of the callbacks can be arrays with multiple functions or just single functions.
 *
 * When successful, the result of the request will be passed to success handlers.
 * When an error occurs, the error object will be passed to error handlers.
 */
export function useFetch(method, initOptions) {
    const reduxDispatch = useDispatch();
    const [shouldRun, setShouldRun] = useState(false);
    const [baseOptions, setBaseOptions] = useState(
        normalizeCallbacks({
            formatResult: (x) => x,
            ...initOptions,
        }),
    );
    const [options, setOptions] = useState(normalizeCallbacks({ ...baseOptions }));
    const [state, dispatch] = useReducer(reducer, {
        data: null,
        error: null,
        isComplete: false,
        isError: false,
        isLoading: false,
        isStarted: false,
        isSuccess: false,
    });

    useEffect(() => {
        const {
            data,
            metadata,
            formatResult,
            onComplete,
            onStart,
            onError,
            onSuccess,
            reduxStart,
            reduxComplete,
            reduxError,
            reduxSuccess,
            url,
            params,
        } = options;

        // If we've just initialized this fetch hook, don't run anything yet.
        if (!shouldRun) {
            return undefined;
        }

        let canceled = false;

        // The magic of async-await lets us do this all inline like redux-saga
        (async () => {
            // Tell our local state that we've started making the request
            dispatch(asyncStart());

            batch(() => {
                reduxStart?.forEach((cb) => reduxDispatch(cb()));
                onStart?.forEach((cb) => cb());
            });

            let result;
            let error;

            const extra = {
                payload: data,
                metadata,
            };

            const ignore = !!(canceled || aborting.current);

            try {
                if (params) {
                    // get method takes "params" to format a querystring
                    result = await method(url, params, data);
                } else {
                    result = await method(url, data);
                }
                result = formatResult(result, extra);

                if (!ignore) {
                    // Tell our local state we succeeded
                    dispatch(asyncSuccess(result, extra));
                }
            } catch (e) {
                if (!ignore) {
                    error = e;
                    rollbar.error(error, { url });

                    // Tell our local state we failed
                    dispatch(asyncError(error, extra));
                }
            } finally {
                if (!ignore) {
                    dispatch(asyncComplete());
                    setShouldRun(false);
                    setOptions(baseOptions);

                    batch(() => {
                        // Run these in finally so that the hook has a chance to finalize
                        // its internal state. Otherwise, because redux has lots of side-effects
                        // (like unmounting components), you could end up with setState errors.
                        if (result) {
                            reduxSuccess?.forEach((cb) => reduxDispatch(cb(result, extra)));
                            onSuccess?.forEach((cb) => cb(result, extra));
                        } else if (error) {
                            reduxError?.forEach((cb) => reduxDispatch(cb(error, extra)));
                            onError?.forEach((cb) => cb(error, extra));
                        }

                        reduxComplete?.forEach((cb) => reduxDispatch(cb({ result, error, extra })));
                        onComplete?.forEach((cb) => cb(result, error, extra));
                    });
                }
            }
        })();

        return () => {
            canceled = true;
        };
    }, [shouldRun, options]);

    // Calling updateOptions will set new base options
    const updateOptions = useCallback(
        (opts = {}) => {
            const newOptions = mergeOptions(baseOptions, opts);
            setBaseOptions(newOptions);
            setOptions(newOptions);
        },
        [baseOptions],
    );

    // Calling runFetch will merge with initial options and REPLACE data
    const runFetch = useCallback(
        (opts = null) => {
            if (opts) {
                setOptions(mergeOptions(baseOptions, opts));
            }

            setShouldRun(true);
        },
        [options],
    );

    const result = [runFetch, state, updateOptions];
    result.isFetchHook = true;

    return result;
}

export const useDelete = (url, options = {}) => useFetch(del, { url, ...options });
export const useGet = (url, options = {}) => useFetch(get, { url, ...options });
export const usePatch = (url, options = {}) => useFetch(patch, { url, ...options });
export const usePost = (url, options = {}) => useFetch(post, { url, ...options });
export const usePostOrPatch = (url, method, options = {}) => {
    const func = method.toLowerCase() === "post" ? post : patch;
    return useFetch(func, { url, ...options });
};

/**
 * Run multiple hooks in a row. Next hook will not run unless previous hook
 * completes successfully first. This allows you to chain fetch hooks (along
 * with regular hooks) to do a buncha stuff synchronously.
 *
 * As an example: You want to run a DELETE on a team member, and afterwards
 * you want to fetch the latest data for the entire team. You'd call this like:
 *
 * const [runSync] = useSync(useRemoveTeamMember(), fetchTeam(), () => console.log('done'));
 * runSync();
 */
export function useSync(...hooks) {
    const [current, setCurrent] = useState(0);
    const [canceled, setCanceled] = useState(false);
    const [hasError, setError] = useState(false);

    const runNext = useCallback(() => {
        if (!canceled && current + 1 <= hooks.length) {
            setCurrent(current + 1);
        }
    }, [current]);

    const runStart = () => {
        setCanceled(false);
        setError(false);
        setCurrent(1);
    };

    useEffect(() => {
        if (!current) {
            return undefined;
        }

        const hook = hooks[current - 1];
        if (hook.isFetchHook) {
            const [runFetchHook] = hook;
            runFetchHook({
                onSuccess: () => runNext(),
            });
        } else {
            const result = hook();
            if (result === false) {
                setError(true);
            } else {
                runNext();
            }
        }

        return () => setCanceled(true);
    }, [current]);

    const { isStarted, isError, isComplete } = hooks.reduce(
        (m, hook) => {
            if (!hook.isFetchHook) {
                return m;
            }

            const [, state] = hook;

            return {
                isStarted: m.isStarted || state.isStarted,
                isError: m.isError || state.isError,
                isComplete: m.isComplete && state.isComplete,
            };
        },
        { isStarted: false, isError: hasError, isComplete: true },
    );

    const updateOptions = hooks.map((h) => (h.isFetchHook ? h[2] : empty));

    return [
        () => runStart(),
        {
            isComplete: isComplete || isError,
            isError,
            isLoading: isStarted && !isComplete && !isError,
        },
        updateOptions,
    ];
}
